// IP合作「出价方案」历史轮次对比 diff 单元格（presentational，供共享表格组件引用）
// 渲染形态（对齐设计稿）：最新值在上（按 diff 类型着色）+ 历史值在下（灰色删除线）垂直堆叠。
//  - added：仅最新值（绿色，无删除线）
//  - edited：最新值（橙色）+ 历史值（灰色删除线）
//  - deleted：仅历史值（灰色删除线）
// 无 diff 时按原样渲染 children（保留既有展示逻辑），调用方无需条件分支。

import React from 'react';
import type { RoundDiffType, RoundDiffValue } from './types';

/** diff 类型 → 展示文字色（Tailwind 任意值类；规范色唯一来源，三表格/图例对齐引用） */
export const DIFF_TEXT_CLASS: Record<RoundDiffType, string> = {
  added: 'text-[#21A364]',
  edited: 'text-[#E08216]',
  deleted: 'text-[#969FB2]',
};

/** 历史值展示色（灰色删除线） */
export const OLD_VALUE_CLASS = 'text-[#969FB2] line-through';

/** 值 → 展示文本（标量/数组通用，对齐只读态 renderBenefitAttrViewValue join 范式；复杂对象走 render 回调） */
function toDisplayText(value: unknown): string {
  if (value === undefined || value === null || value === '') return '--';
  if (Array.isArray(value)) {
    const text = value.map(String).filter(Boolean).join('/');
    return text || '--';
  }
  if (typeof value === 'object') return '';
  return String(value);
}

export interface RoundDiffCellProps {
  /** 该单元格的 diff（无则原样渲染 children） */
  diff?: RoundDiffValue;
  /** 最新值（newValue）自定义渲染（如 CurrencyType / 时长格式化）；缺省走 toDisplayText */
  renderNewValue?: (value: unknown) => React.ReactNode;
  /** 历史值（oldValue）自定义渲染；缺省走 toDisplayText */
  renderOldValue?: (value: unknown) => React.ReactNode;
  /** 无 diff 时的默认渲染（保留既有展示逻辑） */
  children?: React.ReactNode;
}

const RoundDiffCell: React.FC<RoundDiffCellProps> = ({
  diff,
  renderNewValue,
  renderOldValue,
  children,
}) => {
  // 无 diff：原样渲染，调用方既有展示逻辑不受影响
  if (!diff) return <>{children ?? null}</>;

  const renderNew = (v: unknown) =>
    renderNewValue ? renderNewValue(v) : toDisplayText(v);
  const renderOld = (v: unknown) =>
    renderOldValue ? renderOldValue(v) : toDisplayText(v);
  const newClass = DIFF_TEXT_CLASS[diff.type];

  // added：最新轮新增，仅显示最新值（绿色，无历史删除线）
  if (diff.type === 'added') {
    return <span className={newClass}>{renderNew(diff.newValue)}</span>;
  }
  // deleted：历史轮有、最新轮无，仅显示历史值（灰色删除线）
  if (diff.type === 'deleted') {
    return <span className={OLD_VALUE_CLASS}>{renderOld(diff.oldValue)}</span>;
  }
  // edited：最新值（橙色）在上 + 历史值（灰色删除线）在下，垂直堆叠
  return (
    <span className="inline-flex flex-col gap-[2px]">
      <span className={newClass}>{renderNew(diff.newValue)}</span>
      <span className={OLD_VALUE_CLASS}>{renderOld(diff.oldValue)}</span>
    </span>
  );
};

export default RoundDiffCell;
